import { createContext, useContext } from 'react';
import { describedBy } from './utils';

export interface FieldContextValue {
  readonly controlId: string;
  readonly descriptionId: string;
  readonly errorId: string;
  readonly hasError: boolean;
  readonly required: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

export interface FieldControlProps {
  readonly id: string;
  readonly 'aria-describedby': string | undefined;
  readonly 'aria-invalid': true | undefined;
  readonly 'aria-required': true | undefined;
}

export function toControlProps(ctx: FieldContextValue): FieldControlProps {
  return {
    id: ctx.controlId,
    'aria-describedby': describedBy(ctx.descriptionId, ctx.hasError && ctx.errorId),
    'aria-invalid': ctx.hasError || undefined,
    'aria-required': ctx.required || undefined,
  };
}

/**
 * Contrato de acessibilidade de campo de formulário.
 *
 * A ligação rótulo↔controle↔descrição↔erro é a fonte mais comum de falha AA.
 * Aqui ela vem pronta: o controle espalha `controlProps` e não tem como
 * esquecer `aria-describedby` nem `aria-invalid`.
 */
export function useField(): FieldContextValue & { readonly controlProps: FieldControlProps } {
  const ctx = useContext(FieldContext);
  if (!ctx) {
    throw new Error('useField precisa estar dentro de <Field>.');
  }
  return { ...ctx, controlProps: toControlProps(ctx) };
}

/**
 * Igual a `useField()`, porém devolve `null` fora de um `<Field>`.
 *
 * Os controles do design system também são usados soltos (numa barra de
 * filtros, por exemplo); nesses casos o rótulo é responsabilidade de quem usa.
 */
export function useFieldOptional():
  | (FieldContextValue & { readonly controlProps: FieldControlProps })
  | null {
  const ctx = useContext(FieldContext);
  if (!ctx) return null;
  return { ...ctx, controlProps: toControlProps(ctx) };
}
